import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    Upload,
} from 'antd';

import { PlusOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';

import type { Sleeves } from "../../../../types/Sleeves";

interface SleeveModalProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void; // Callback after successful submit
    initialValues?: Sleeves | null;
    isEditMode?: boolean;
    sleeves: Sleeves[];
}

const SleeveModal: React.FC<SleeveModalProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
    sleeves,
}) => {
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);

    const [imageFile, setImageFile] = useState<File | null>(null);
    const [profileImageFileList, setProfileImageFileList] = useState<UploadFile[]>([]);


    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');

    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };


    // image preview 
    const handlePreview = async (file: UploadFile) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj as File);
        }

        setPreviewImage(file.url || (file.preview as string));
        setPreviewOpen(true);
        setPreviewTitle(file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1));
    };

    // Utility function to convert image to base64
    const getBase64 = (file: File): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });


    // Handle form submit
    const handleAddSleeve = async (values: any) => {
        try {
            const formData = new FormData();

            formData.append('sleeve_name', values.sleeve_name);
            formData.append('sleeve_mesh_name', values.sleeve_mesh_name);

            if (imageFile) {
                formData.append('attachments', imageFile); // ✅ binary file
            }

            if (initialValues?.sleeve_id) {
                formData.append('sleeve_id', initialValues.sleeve_id.toString());
            }
            const url = isEditMode
                ? 'edit-sleeve'
                : 'add-sleeve';

            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: formData,
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            console.log(formData);

            onCreate(data); // Notify parent about success
            showSuccess(
                isEditMode ? 'Sleeve updated successfully!' : 'Sleeve created successfully!',
            );
            form.resetFields();
            if (!isModalClose) {
                setImageFile(null);
                setProfileImageFileList([]);
            }

            if (isModalClose) {
                onCancel(); // Close modal only if Submit button was clicked (not Submit & Create New)
            }
        } catch (error) {
            showError(`Failed to ${isEditMode ? 'update' : 'create'} sleeve`);
        }
    };

    // When modal opens or mode changes, set/reset form fields accordingly
    useEffect(() => {
        if (visible && isEditMode && initialValues) {
            form.setFieldsValue({
                sleeve_name: initialValues.sleeve_name,
                sleeve_mesh_name: initialValues.sleeve_mesh_name,
            });
            if (initialValues.sleeve_image) {
                const imageUrl = `${initialValues.sleeve_image}`;
                setProfileImageFileList([
                    {
                        uid: '-1',
                        name: 'image.png',
                        status: 'done',
                        url: imageUrl,
                    },
                ]);
                setImageFile(null); // prevent old file being reused
            }
        } else if (visible) {
            form.resetFields();
        }
    }, [visible, isEditMode, initialValues, form]);


    const uploadButton = (
        <div
            style={{
                width: 220,
                height: 220,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <PlusOutlined style={{ fontSize: 24 }} />
            <div style={{ marginTop: 8 }}>Upload</div>
        </div>
    );

    return (
        <>
            <Modal
                title={isEditMode ? 'Edit Sleeve' : 'Add New Sleeve'}
                visible={visible}
                onCancel={onCancel}
                footer={null}
                width="90%"
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleAddSleeve}
                    autoComplete="off"
                >
                    <Row gutter={16}>
                        
                        <Col span={12}>
                            <Form.Item
                                name="sleeve_name"
                                className='px-8'
                                label={
                                    <span>
                                        Sleeve &nbsp;
                                        <span style={{ fontSize: '12px', color: '#888' }}>
                                            (Max 100 characters)
                                        </span>
                                    </span>
                                }
                                rules={[
                                    { required: true, message: 'Please enter the Sleeve Name' },
                                    { max: 100, message: 'Maximum 100 characters allowed' },
                                ]}
                            >
                                <Input
                                    showCount
                                    maxLength={100}
                                    placeholder="e.g., Sleeve Name"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="sleeve_mesh_name"
                                className='px-8'
                                label={
                                    <span>
                                        Sleeve Mesh Name &nbsp;
                                        <span style={{ fontSize: '12px', color: '#888' }}>
                                            (Only Developer Purpose)(Max 100 characters)
                                        </span>
                                    </span>
                                }
                                rules={[
                                    { required: true, message: 'Please enter the Sleeve Mesh Name' },
                                    { max: 100, message: 'Maximum 100 characters allowed' },
                                ]}
                            >
                                <Input
                                    showCount
                                    maxLength={100}
                                    placeholder="e.g., Sleeve Mesh Name"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label="Upload Sleeve Image" required>
                                <Upload
                                    beforeUpload={() => false}
                                    listType="picture-card"
                                    maxCount={1}
                                    fileList={profileImageFileList}
                                    onPreview={handlePreview} // 👈 Add this
                                    onChange={({ fileList }) => {
                                        setProfileImageFileList(fileList);

                                        const rawFile = fileList[0]?.originFileObj;
                                        if (rawFile instanceof File) {
                                            setImageFile(rawFile); // ✅ set real File here
                                        } else {
                                            setImageFile(null);
                                        }
                                    }}
                                >
                                    {profileImageFileList.length >= 1 ? null : uploadButton}
                                </Upload>
                            </Form.Item>
                        </Col>
                    </Row>


                    <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Space>
                            <Button onClick={onCancel}>Cancel</Button>
                            {!isEditMode && <Button
                                type="primary"
                                htmlType="submit"
                                onClick={() => setIsModalClose(false)}
                                disabled={isSubmitDisabled()}
                            >
                                Submit & Create New Sleeve
                            </Button>}
                            <Button
                                type="primary"
                                htmlType="submit"
                                onClick={() => setIsModalClose(true)}
                                disabled={isSubmitDisabled()}
                            >
                                Submit
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                visible={previewOpen}
                title={previewTitle}
                footer={null}
                centered
                onCancel={() => setPreviewOpen(false)}
            >
                <img
                    alt="Preview"
                    style={{ width: '100%' }}
                    src={previewImage}
            />
            </Modal>
        </>
    );
};

export default SleeveModal;
